import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppHeader, AppShell, Banner, Breadcrumbs, Button, cn } from '@beatq/ui';
import { parseAccessTokenClaims, useAuth, usePermissions } from '@beatq/shared';
import { getEmergencyStatus } from '../features/auth/adminApi.js';
import {
  ADMIN_NAV_GROUPS,
  filterAdminNavGroups,
  isSuperAdminOperator,
  nestedNavChildren,
  topLevelNavItems,
} from './adminNav.js';
import { buildSubjectTrail } from './subjectTrail.js';
import { useAdminPlatformRealtime } from '../features/auth/useAdminPlatformRealtime.js';
import { AdminCommandPalette } from './AdminCommandPalette.js';
import { AdminAccountMenu } from './AdminAccountMenu.js';
import { AdminNotificationsBell } from './AdminNotificationsBell.js';
import { LiveMarker } from './LiveMarker.js';
import { AdminIcon } from './adminIcons.js';
import { ChefHat, ChevronDown, Menu, X } from 'lucide-react';
import { TenantImpersonationBanner } from './TenantImpersonationBanner.js';
import {
  getImpersonationEndedGuard,
  getStoredOperatorEmail,
  recordImpersonationVisit,
  shouldBlockImpersonatedBack,
  dismissImpersonationExpiryNotice,
} from './adminSession.js';
import {
  getStoredImpersonationSessionId,
  shouldBlockImpersonationReentry,
} from '../features/entity-360/entity360State.js';

function navClassName(isActive: boolean, emergency = false, nested = false) {
  return cn(
    'flex items-center gap-inline rounded-control px-field py-inline text-body no-underline min-h-11',
    nested && 'ml-field text-body-sm',
    isActive ? 'bg-selected font-medium text-primary' : 'text-muted hov:bg-sunken',
    emergency &&
      !isActive &&
      'a01-emergency-link border-2 border-solid border-current bg-sunken font-medium hov:bg-page',
    emergency && isActive && 'a01-emergency-link border-2 border-solid border-current bg-selected',
  );
}

export function AdminShell({ banners }: { banners?: ReactNode } = {}) {
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const { hasPerm, loading: permsLoading, authenticated } = usePermissions();
  const { accessToken } = useAuth();
  const realtime = useAdminPlatformRealtime();
  const sessionId = getStoredImpersonationSessionId();
  const environmentLabel = import.meta.env.PROD ? 'Production' : 'Development';

  const superAdmin = isSuperAdminOperator({
    email: getStoredOperatorEmail(),
    roles: parseAccessTokenClaims(accessToken).rol,
  });

  const emergencyQuery = useQuery({
    queryKey: ['admin', 'shell', 'emergency-status'],
    queryFn: getEmergencyStatus,
    refetchInterval: 30_000,
  });

  const emergencyActive = useMemo(() => {
    const env = emergencyQuery.data;
    if (env == null || typeof env !== 'object') return false;
    const data = (env as { data?: { active?: boolean; status?: string } }).data ?? env;
    const d = data as { active?: boolean; status?: string };
    return d.active === true || d.status === 'active' || d.status === 'PAUSED';
  }, [emergencyQuery.data]);

  const groups = useMemo(
    () =>
      filterAdminNavGroups(ADMIN_NAV_GROUPS, hasPerm, authenticated && !permsLoading, superAdmin),
    [hasPerm, authenticated, permsLoading, superAdmin],
  );
  const mainGroups = groups.filter((g) => !g.pinBottom);
  const emergencyGroup = groups.find((g) => g.pinBottom) ?? null;
  const emergencyItem = emergencyGroup?.items[0] ?? null;

  const trail = buildSubjectTrail(location.pathname, searchParams);
  const [expiryNotice, setExpiryNotice] = useState(
    () => getImpersonationEndedGuard()?.notice === 'expired',
  );

  useEffect(() => {
    if (getImpersonationEndedGuard()?.notice === 'expired') {
      setExpiryNotice(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (sessionId) recordImpersonationVisit(location.pathname);
  }, [sessionId, location.pathname]);

  useEffect(() => {
    const guard = getImpersonationEndedGuard();
    if (!guard) return;
    if (shouldBlockImpersonatedBack(location.pathname, guard)) {
      window.location.replace(guard.returnHref);
      return;
    }
    if (shouldBlockImpersonationReentry()) {
      window.location.replace(guard.returnHref);
    }
  }, [location.pathname]);

  useEffect(() => {
    const guard = getImpersonationEndedGuard();
    if (!guard) return undefined;
    function onPop() {
      const current = getImpersonationEndedGuard();
      if (
        shouldBlockImpersonatedBack(window.location.pathname, current) ||
        shouldBlockImpersonationReentry()
      ) {
        window.location.replace(current?.returnHref ?? '/admin-dashboard');
      }
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const shellBanners = (
    <>
      <TenantImpersonationBanner pathname={location.pathname} searchParams={searchParams} />
      {expiryNotice ? (
        <Banner label="Impersonation ended" tone="info">
          <span className="flex flex-wrap items-center gap-field">
            <span>The impersonation session ended. You are back on the case.</span>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                dismissImpersonationExpiryNotice();
                setExpiryNotice(false);
              }}
            >
              Dismiss
            </Button>
          </span>
        </Banner>
      ) : null}
      {emergencyActive ? (
        <Banner label="Platform emergency" tone="negative">
          <span className="flex flex-wrap items-center gap-field">
            <span>Emergency controls are active on the platform.</span>
            <Link to="/emergency-controls" className="underline">
              Open emergency controls
            </Link>
          </span>
        </Banner>
      ) : null}
      {banners}
    </>
  );

  return (
    <div className="admin-console">
      <AppShell
        banners={shellBanners}
        header={
          <AppHeader
            className="admin-header"
            productName="BeatQ Eats Admin"
            homeHref="/admin-dashboard"
            renderBrand={({ homeHref }) => (
              <div className="admin-brand-area">
                <button
                  type="button"
                  className="admin-mobile-toggle"
                  aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
                  aria-expanded={mobileNavOpen}
                  aria-controls="admin-primary-nav"
                  onClick={() => setMobileNavOpen(!mobileNavOpen)}
                >
                  {mobileNavOpen ? <X size={21} /> : <Menu size={21} />}
                </button>
                <Link to={homeHref} className="admin-brand">
                  <span className="admin-brand__mark">
                    <ChefHat size={26} aria-hidden="true" />
                  </span>
                  <span>
                    <strong>BeatQ Eats</strong>
                    <small>Admin Portal</small>
                  </span>
                </Link>
              </div>
            )}
            actions={
              <div className="admin-header__actions">
                <AdminCommandPalette />
                <div className="a01-header-status" aria-label={`${environmentLabel} environment`}>
                  <span className="a01-env">{environmentLabel}</span>
                  <LiveMarker connected={realtime.connected} />
                </div>
                <AdminNotificationsBell />
                <AdminAccountMenu />
              </div>
            }
          />
        }
        nav={
          <nav
            id="admin-primary-nav"
            data-mobile-open={mobileNavOpen}
            aria-label="Admin primary"
            className={cn(
              'admin-sidebar flex shrink-0 flex-col gap-xs border-b border-line bg-raised p-inline',
              'md:sticky md:top-0 md:w-60 md:border-r md:border-b-0',
              'md:h-[calc(100vh-3.5rem)] md:max-h-[calc(100vh-3.5rem)] md:overflow-hidden',
            )}
          >
            <ul className="m-0 flex min-h-0 list-none flex-col gap-section overflow-y-auto p-0 flex-1">
              {mainGroups.map((group) => {
                const top = topLevelNavItems(group.items);
                return (
                  <li key={group.id} className="a01-nav-group">
                    {group.id !== 'emergency' && group.id !== 'dashboard' ? (
                      <button
                        type="button"
                        className="a01-nav-group__label"
                        aria-expanded={!collapsedGroups.includes(group.id)}
                        aria-controls={`admin-nav-${group.id}`}
                        onClick={() =>
                          setCollapsedGroups((current) =>
                            current.includes(group.id)
                              ? current.filter((id) => id !== group.id)
                              : [...current, group.id],
                          )
                        }
                      >
                        <AdminIcon screenId={top[0]?.screenId} size={18} />
                        {group.label}
                        <ChevronDown size={14} aria-hidden="true" />
                      </button>
                    ) : null}
                    <ul
                      id={`admin-nav-${group.id}`}
                      hidden={collapsedGroups.includes(group.id)}
                      className="m-0 mt-xs flex list-none flex-col gap-xs p-0"
                    >
                      {top.map((item) => {
                        const nested = nestedNavChildren(group.items, item.screenId);
                        return (
                          <li key={item.href}>
                            <NavLink
                              to={item.href}
                              className={({ isActive }) => navClassName(isActive)}
                              end={item.href === '/admin-dashboard'}
                              data-nav-screen={item.screenId}
                            >
                              <AdminIcon screenId={item.screenId} href={item.href} size={18} />
                              <span>{item.label}</span>
                            </NavLink>
                            {nested.length > 0 ? (
                              <ul className="a01-nav-nested m-0 mt-xs flex list-none flex-col gap-xs p-0">
                                {nested.map((child) => (
                                  <li key={child.href}>
                                    <NavLink
                                      to={child.href}
                                      className={({ isActive }) =>
                                        navClassName(isActive, false, true)
                                      }
                                      data-nav-screen={child.screenId}
                                    >
                                      <span>{child.label}</span>
                                    </NavLink>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
            {emergencyItem ? (
              <ul className="a01-emergency m-0 mt-auto flex shrink-0 list-none flex-col gap-xs border-t-2 border-current pt-inline p-0">
                <li>
                  <NavLink
                    to={emergencyItem.href}
                    className={({ isActive }) => navClassName(isActive, true)}
                    data-nav-screen={emergencyItem.screenId}
                  >
                    <AdminIcon
                      screenId={emergencyItem.screenId}
                      href={emergencyItem.href}
                      size={18}
                    />
                    <span>{emergencyItem.label}</span>
                  </NavLink>
                </li>
              </ul>
            ) : null}
          </nav>
        }
      >
        {trail.length > 0 ? (
          <div className="a01-subject-trail mb-field">
            <Breadcrumbs
              items={trail}
              renderLink={({ href, label }) => (
                <Link to={href} className="text-body-sm">
                  {label}
                </Link>
              )}
            />
          </div>
        ) : null}
        <Outlet />
      </AppShell>
    </div>
  );
}
